/**
 * @file Helper for Slideshow
 */

import { __, np__, p__ } from '@@/bits/intl'

/**
 * Build a link to a slideshow
 * @param showLink - Link to the underlying padlet
 */
const buildSlideshowLink = (showLink: string, wishId?: number | string): string => {
  return `${showLink}/slideshow${wishId === undefined ? '' : `/wish/${wishId}`}`
}

const DEFAULT_QR_CODE_SIZE = 1059

const secondDurationLong = (number: number): string => {
  return np__(
    'Represents duration. Used for configuration. For example: 3 seconds',
    '%{number} second',
    '%{number} seconds',
    number,
    { number },
  )
}

const secondDurationShort = (number: number): string => {
  return p__('Represents duration. Used for configuration. For example: 3s', '%{number}s', { number })
}

const secondDurationText = (durationType: string): string => {
  if (durationType === 'auto') {
    return __('Auto')
  } else {
    return __('Auto-slow')
  }
}

const AUTOPLAY_DURATION_TYPES = [1, 2, 3, 5, 15, 30, 60, 'auto', 'auto_slow'] as const
const AUTOPLAY_DURATIONS = AUTOPLAY_DURATION_TYPES.reduce((acc, duration) => {
  acc[duration] = {
    long: typeof duration === 'number' ? secondDurationLong(duration) : secondDurationText(duration),
    short: typeof duration === 'number' ? secondDurationShort(duration) : secondDurationText(duration),
  }
  return acc
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
}, {} as Record<typeof AUTOPLAY_DURATION_TYPES[number], { long: string; short: string }>)

type AutoplayDurationType = keyof typeof AUTOPLAY_DURATIONS

const MIN_SLIDE_DURATION_MS = 2000
const SLIDE_DURATION_PER_WORD_MS = 200
const SLIDE_DURATION_PER_ATTACHMENT_MS = 2000

const calculateAutoSlideDuration = (numWords: number, numAttachments: number): number => {
  return Math.max(
    MIN_SLIDE_DURATION_MS,
    SLIDE_DURATION_PER_ATTACHMENT_MS * numAttachments + SLIDE_DURATION_PER_WORD_MS * numWords,
  )
}

const SLIDE_SLOW_DURATION_PER_WORD_MS = 500
const SLIDE_SLOW_DURATION_PER_ATTACHMENT_MS = 5000
const calculateAutoSlowSlideDuration = (numWords: number, numAttachments: number): number => {
  return (
    MIN_SLIDE_DURATION_MS +
    SLIDE_SLOW_DURATION_PER_ATTACHMENT_MS * numAttachments +
    SLIDE_SLOW_DURATION_PER_WORD_MS * numWords
  )
}

const getCurrentPostCid = (entityId: string): string => `c${entityId}`

const NOTIFICATION_MESSAGES = {
  stoppedAutoplaying: __('Autoplay stopped'),
  startedAutoplayingForTouchDevice: __('Autoplay started, press pause to stop'),
  startedAutoplayingForMouseDevice: __('Autoplay started, press any key to stop'),
}

export {
  AUTOPLAY_DURATIONS,
  AutoplayDurationType,
  DEFAULT_QR_CODE_SIZE,
  NOTIFICATION_MESSAGES,
  buildSlideshowLink,
  calculateAutoSlideDuration,
  calculateAutoSlowSlideDuration,
  getCurrentPostCid,
}
